import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { HelpCircle, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "bug", label: "I found a bug or something isn't working", placeholder: "Describe what happened and what you expected..." },
  { value: "question", label: "I have a question about this page", placeholder: "What would you like to know..." },
  { value: "consult", label: "I'd like to consult before submitting a Work Order", placeholder: "Tell us about the role you're considering..." },
  { value: "feedback", label: "General feedback", placeholder: "Share your thoughts..." },
];

export default function GetHelpButton() {
  const { api, user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!category || !content.trim()) { toast.error("Select a category and enter a message"); return; }
    setSending(true);
    try {
      const searchMatch = location.pathname.match(/\/search\/(.+)/);
      const woMatch = location.pathname.match(/\/work-orders\/(.+)/);
      await api.post("/support-ticket", {
        content, category,
        metadata: {
          page: location.pathname,
          search_id: searchMatch?.[1] || null,
          work_order_id: woMatch?.[1] || null,
          linked_entity_type: searchMatch ? "role_search" : woMatch ? "work_order" : null,
          linked_entity_id: searchMatch?.[1] || woMatch?.[1] || null,
          ticket_category: category,
          user_agent: navigator.userAgent,
        }
      });
      setSent(true);
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const reset = () => { setCategory(""); setContent(""); setSent(false); setOpen(false); };
  const selected = CATEGORIES.find(c => c.value === category);

  if (user?.role === "master_admin") return null;

  return (
    <>
      {/* Floating button */}
      <button onClick={() => setOpen(!open)} data-testid="get-help-btn"
        className="fixed bottom-20 md:bottom-6 left-6 z-40 w-10 h-10 bg-white border border-slate-200 shadow-md rounded-full flex items-center justify-center text-slate-500 hover:text-slate-700 hover:shadow-lg transition-all">
        {open ? <X className="w-4 h-4" /> : <HelpCircle className="w-5 h-5" />}
      </button>

      {/* Slide-up panel */}
      {open && (
        <div className="fixed bottom-20 md:bottom-20 left-6 z-40 w-80 bg-white rounded-xl border border-slate-200 shadow-xl animate-slideUp overflow-hidden" data-testid="help-panel">
          <div className="h-1 bg-gradient-to-r from-evergreen-950 via-lime-600 to-teal-500" />
          {sent ? (
            <div className="p-6 text-center">
              <CheckCircle2 className="w-10 h-10 text-lime-600 mx-auto mb-3" />
              <p className="text-sm font-heading text-slate-900 mb-1">Message received</p>
              <p className="text-xs text-slate-500 mb-4">Expect a reply within 1 business day.</p>
              <Button variant="outline" size="sm" onClick={reset}>Close</Button>
            </div>
          ) : (
            <div className="p-4">
              <h3 className="text-sm font-heading text-slate-900 mb-3">How can we help?</h3>
              <div className="space-y-2 mb-3">
                {CATEGORIES.map(c => (
                  <label key={c.value} className={`flex items-start gap-2.5 p-2 rounded-lg cursor-pointer transition-colors ${category === c.value ? "bg-lime-50 border border-lime-200" : "hover:bg-slate-50 border border-transparent"}`}>
                    <input type="radio" name="category" value={c.value} checked={category === c.value} onChange={() => setCategory(c.value)} className="mt-0.5 accent-evergreen-950" />
                    <span className="text-xs text-slate-700">{c.label}</span>
                  </label>
                ))}
              </div>
              <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder={selected?.placeholder || "Tell us more..."} className="text-sm min-h-[80px] mb-3" data-testid="help-message" />
              <Button onClick={handleSubmit} disabled={sending} className="w-full bg-evergreen-950 hover:bg-evergreen-900 text-white text-sm" data-testid="help-submit">
                {sending ? "Sending..." : "Send"}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
